export class Tab {
    constructor(element) {
        this.element = element;

        // Tab controls container (expected to hold the tab triggers)
        this.tabList = this.element.querySelector('.js-tabs__controls');
        this.listItems = Array.from(this.tabList.querySelectorAll('li'));
        this.triggers = Array.from(this.tabList.querySelectorAll('a'));

        // Panels container and individual panels
        this.panelsList = this.element.querySelector('.js-tabs__panels');
        this.panels = Array.from(this.panelsList.children).filter(child =>
            child.classList.contains('js-tabs__panel')
        );

        // Optional class names and settings pulled from data attributes
        this.hideClass = this.element.getAttribute('data-hide-panel-class') || 'js-tabs__panel--hidden';
        this.customShowClass = this.element.getAttribute('data-show-panel-class') || false;
        this.layout = this.element.getAttribute('data-tabs-layout') || 'horizontal';
        this.deepLinkOn = this.element.getAttribute('data-deep-link') === 'on';

        // Initialisation routines
        this.initTab();
        this.initExternalTrigger();
        this.initPrevNextButtons();
    }

    // Initialises ARIA roles, default state, and event listeners
    initTab() {
        if (!this.tabList || !this.panelsList || this.triggers.length === 0 || this.panels.length === 0) {
            console.warn('Tabs setup incomplete. Ensure proper markup with .js-tabs__controls and .js-tabs__panels.');
            return;
        }

        if (this.triggers.length !== this.panels.length) {
            console.warn('Number of tab triggers and panels do not match.');
        }

        this.tabList.setAttribute('role', 'tablist');
        this.element.classList.add('tabs--no-interaction');

        // Set up each tab trigger and corresponding panel
        this.triggers.forEach((trigger, index) => {
            const isSelected = index === 0;
            const panel = this.panels[index];
            const panelId = panel.getAttribute('id');

            this.listItems[index].setAttribute('role', 'presentation');

            trigger.setAttribute('role', 'tab');
            trigger.setAttribute('aria-selected', isSelected);
            trigger.setAttribute('aria-controls', panelId);
            trigger.setAttribute('id', `tab-${panelId}`);
            trigger.classList.add('js-tabs__trigger');
            if (!isSelected) trigger.setAttribute('tabindex', '-1');

            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('aria-labelledby', `tab-${panelId}`);
            panel.classList.toggle(this.hideClass, !isSelected);
            if (isSelected && this.customShowClass) panel.classList.add(this.customShowClass);
        });

        this.initTabEvents();
        this.initDeepLink();
    }

    // Sets up keyboard and click event listeners for tab navigation
    initTabEvents() {
        this.tabList.addEventListener('click', (event) => {
            const trigger = event.target.closest('.js-tabs__trigger');
            if (trigger) this.triggerTab(trigger, event);
        });

        this.tabList.addEventListener('keydown', (event) => {
            if (!event.target.closest('.js-tabs__trigger')) return;

            if (tabNavigateNext(event, this.layout)) {
                event.preventDefault();
                this.selectNewTab('next');
            } else if (tabNavigatePrev(event, this.layout)) {
                event.preventDefault();
                this.selectNewTab('prev');
            }
        });
    }

    // Switches to the next or previous tab based on direction
    selectNewTab(direction, currentIndex = null) {
        const selectedTab = this.tabList.querySelector('[aria-selected="true"]');
        let index = currentIndex !== null ? currentIndex : this.triggers.indexOf(selectedTab);

        index = direction === 'next' ? index + 1 : index - 1;

        // Wrap around if out of bounds
        if (index < 0) index = this.listItems.length - 1;
        if (index >= this.listItems.length) index = 0;

        this.triggerTab(this.triggers[index]);
        this.triggers[index].focus();
    }

    // Triggers a tab change manually, optionally from an event
    triggerTab(tabTrigger, event) {
        if (event) event.preventDefault();
        const index = this.triggers.indexOf(tabTrigger);

        // Do nothing if the tab is already selected
        if (this.triggers[index].getAttribute('aria-selected') === 'true') return;

        this.element.classList.remove('tabs--no-interaction');

        this.triggers.forEach((trigger, i) => {
            const isActive = i === index;

            // Toggle panel visibility
            this.panels[i].classList.toggle(this.hideClass, !isActive);
            if (this.customShowClass) {
                this.panels[i].classList.toggle(this.customShowClass, isActive);
            }

            // Update ARIA and tabindex for accessibility
            trigger.setAttribute('aria-selected', isActive);
            trigger.setAttribute('tabindex', isActive ? '0' : '-1');
        });

        // Update the URL hash for deep linking
        if (this.deepLinkOn) {
            history.replaceState(null, '', `#${tabTrigger.getAttribute('aria-controls')}`);
        }
    }

    // Enables hash-based deep linking to a specific tab panel
    initDeepLink() {
        if (!this.deepLinkOn) return;

        const hash = window.location.hash.substr(1);
        if (!hash) return;

        for (let i = 0; i < this.panels.length; i++) {
            if (this.panels[i].getAttribute('id') === hash) {
                this.triggerTab(this.triggers[i], false);
                setTimeout(() => {
                    this.panels[i].scrollIntoView(true);
                });
                break;
            }
        }
    }

    // Allows external buttons to open a tab by ID (e.g., from another component)
    initExternalTrigger() {
        const button = document.querySelector('.external-tab-trigger');
        if (button) {
            button.addEventListener('click', () => {
                const targetId = button.getAttribute('data-target-id');
                this.openTabById(targetId);
            });
        }
    }

    // Activates a tab based on the panel ID it controls
    openTabById(panelId) {
        const tabTrigger = this.triggers.find(trigger =>
            trigger.getAttribute('aria-controls') === panelId
        );
        if (tabTrigger) {
            this.triggerTab(tabTrigger);
            tabTrigger.focus();
        }
    }

    // Adds click listeners to "prev" and "next" buttons within each tab panel
    initPrevNextButtons() {
        this.panels.forEach((panel, index) => {
            const prevBtn = panel.querySelector('[data-prev-btn]');
            const nextBtn = panel.querySelector('[data-next-btn]');

            if (prevBtn) {
                prevBtn.addEventListener('click', () => {
                    this.selectNewTab('prev', index);
                });
            }

            if (nextBtn) {
                nextBtn.addEventListener('click', () => {
                    this.selectNewTab('next', index);
                });
            }
        });
    }
}

// Helper: Check if keyboard event should navigate to next tab
function tabNavigateNext(event, layout) {
    return (layout === 'horizontal' && (event.key === 'ArrowRight' || event.keyCode === 39)) ||
           (layout === 'vertical' && (event.key === 'ArrowDown' || event.keyCode === 40));
}

// Helper: Check if keyboard event should navigate to previous tab
function tabNavigatePrev(event, layout) {
    return (layout === 'horizontal' && (event.key === 'ArrowLeft' || event.keyCode === 37)) ||
           (layout === 'vertical' && (event.key === 'ArrowUp' || event.keyCode === 38));
}